import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, nairaFormat } from "@/lib/api";
import { imgUrl } from "@/components/ImageUploader";
import { Button } from "@/components/ui/button";
import { ArrowLeft, MapPin, Mail, Users, CheckCircle2, ExternalLink } from "lucide-react";

const CATEGORY_LABELS = {
  musician: "Musician", actor: "Actor", photographer: "Photographer", videographer: "Videographer",
  model: "Model", dancer: "Dancer", comedian: "Comedian", mc: "MC / Host", dj: "DJ",
  influencer: "Influencer", designer: "Designer", other: "Creative",
};

export default function CreativeProfile() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/creatives/${id}`)
      .then(({ data }) => setC(data))
      .catch(() => setError("Profile not found or not yet public"));
  }, [id]);

  if (error) return (
    <div className="min-h-screen bg-[#030A06] text-white">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-20 text-center">
        <h1 className="font-display font-black text-4xl">404</h1>
        <p className="text-[#8A9F90] mt-2">{error}</p>
        <Link to="/creatives" className="text-[#00E676] underline mt-4 inline-block">Back to directory</Link>
      </div>
    </div>
  );

  if (!c) return <div className="min-h-screen bg-[#030A06] text-[#8A9F90] flex items-center justify-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#030A06] text-white">
      <Header />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/creatives" className="text-[#8A9F90] hover:text-[#00E676] text-sm flex items-center gap-1 mb-6" data-testid="back-to-directory">
          <ArrowLeft className="w-4 h-4" /> All creators
        </Link>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* LEFT: Profile photo + identity */}
          <div className="lg:col-span-4">
            <div className="aspect-square bg-[#07140B] border border-[#122E1C] overflow-hidden">
              {c.profile_image_path ? (
                <img src={imgUrl(c.profile_image_path)} alt={c.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-display font-black text-9xl text-[#0D2114]">{c.name?.[0]?.toUpperCase()}</div>
              )}
            </div>

            <div className="mt-6 space-y-3">
              {c.verified && (
                <div className="flex items-center gap-2 text-[#00E676] text-sm font-mono-jb"><CheckCircle2 className="w-4 h-4" /> Verified by Nasarawa Expo</div>
              )}
              {c.location && <div className="flex items-center gap-2 text-[#8A9F90] text-sm"><MapPin className="w-4 h-4" /> {c.location}</div>}
              {c.total_followers > 0 && (
                <div className="flex items-center gap-2 text-[#F4B41A] text-sm font-mono-jb">
                  <Users className="w-4 h-4" /> {Number(c.total_followers).toLocaleString()} combined followers
                </div>
              )}
            </div>

            <a href="mailto:hello@nasarawaexpo.com?subject=Book%20a%20creator" data-testid="book-creator-btn">
              <Button className="w-full mt-6 bg-[#00E676] hover:bg-[#00C853] text-[#030A06] font-bold rounded-none py-6">
                <Mail className="w-4 h-4 mr-2" /> Book through Nasarawa Expo
              </Button>
            </a>
            <p className="text-xs text-[#8A9F90] mt-2 text-center">All bookings go through the agency. We handle the brief, payment & follow-up.</p>
          </div>

          {/* RIGHT: name, bio, services, portfolio */}
          <div className="lg:col-span-8">
            <div className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-[#F4B41A]">// {CATEGORY_LABELS[c.category] || "Creative"}</div>
            <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-2">{c.name}</h1>
            {c.bio && <p className="mt-6 text-lg text-[#8A9F90] leading-relaxed max-w-2xl">{c.bio}</p>}

            {/* Social handles */}
            {(c.social_handles || []).length > 0 && (
              <div className="mt-8">
                <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#8A9F90] mb-3">// Where you can find them</h3>
                <div className="flex flex-wrap gap-2">
                  {c.social_handles.map((h, i) => (
                    <a key={i} href={h.url || "#"} target="_blank" rel="noreferrer" data-testid={`social-${h.platform}`} className="border border-[#122E1C] hover:border-[#00E676] px-3 py-2 text-xs font-mono-jb uppercase flex items-center gap-2 text-[#8A9F90] hover:text-[#00E676]">
                      <span className="text-[#F4B41A]">{h.platform}</span> @{h.handle}
                      <span className="text-[#F4B41A]">{Number(h.followers || 0).toLocaleString()}</span>
                      {h.url && <ExternalLink className="w-3 h-3" />}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Services */}
            {(c.services || []).length > 0 && (
              <div className="mt-10">
                <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#00E676] mb-4">// Services & rates</h3>
                <div className="space-y-px bg-[#122E1C]">
                  {c.services.map((s, i) => (
                    <div key={i} data-testid={`service-${i}`} className="bg-[#07140B] p-5 flex flex-wrap items-start justify-between gap-4 lift">
                      <div className="flex-1 min-w-[200px]">
                        <h4 className="font-display font-bold text-xl tracking-tight">{s.name}</h4>
                        {s.description && <p className="text-sm text-[#8A9F90] mt-1">{s.description}</p>}
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[#8A9F90] font-mono-jb">Starting at</div>
                        <div className="font-mono-jb text-2xl text-[#00E676] font-bold">{nairaFormat(s.price_ngn)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Portfolio */}
            {(c.portfolio_paths || []).length > 0 && (
              <div className="mt-10">
                <h3 className="font-mono-jb text-[10px] uppercase tracking-[0.2em] text-[#F4B41A] mb-4">// Portfolio</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {c.portfolio_paths.map((p, i) => (
                    <a key={i} href={imgUrl(p)} target="_blank" rel="noreferrer" data-testid={`portfolio-${i}`} className="block aspect-square bg-[#07140B] overflow-hidden border border-[#122E1C] hover:border-[#00E676]">
                      <img src={imgUrl(p)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
